import { Menu, Plus, Share2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface ChatHeaderProps {
  title?: string;
  showMenuButton?: boolean;
  onMenuClick?: () => void;
  onNewChat?: () => void;
  onShare?: () => void;
  className?: string;
}

/**
 * ChatHeader - Chat area top navigation bar
 *
 * Features:
 * - Show menu button on mobile
 * - Display current conversation title
 * - New chat / share buttons
 */
export function ChatHeader({
  title,
  showMenuButton = false,
  onMenuClick,
  onNewChat,
  onShare,
  className,
}: ChatHeaderProps) {
  const { t } = useTranslation();
  const displayTitle = title ?? t('sidebar:history.newConversation');

  return (
    <TooltipProvider>
      <header
        className={cn(
          'flex h-14 items-center justify-between border-b border-gray-100 bg-white px-4',
          className
        )}
      >
        {/* Left: Menu button (mobile) + Title */}
        <div className="flex items-center gap-3">
          {showMenuButton && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onMenuClick}
              className="h-9 w-9 text-gray-600 hover:bg-gray-100 hover:text-gray-900 md:hidden"
            >
              <Menu className="h-5 w-5" />
            </Button>
          )}
          <h1 className="max-w-[200px] truncate text-base font-semibold text-gray-900 sm:max-w-none">
            {displayTitle}
          </h1>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-1">
          {onNewChat && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onNewChat}
                  className="h-9 w-9 text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('sidebar:history.newConversation')}</TooltipContent>
            </Tooltip>
          )}

          {onShare && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onShare}
                  className="h-9 w-9 text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                >
                  <Share2 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('common:share')}</TooltipContent>
            </Tooltip>
          )}
        </div>
      </header>
    </TooltipProvider>
  );
}
